import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLenis } from "lenis/react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/App";
import { translations } from "@/i18n";

const Header = () => {
  const { lang, setLang } = useLanguage();
  const t = translations[lang];
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const lenis = useLenis();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (lenis) lenis.scrollTo(id, { offset: -72 });
    else document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const links = [
    ["#services", t.nav.services, "nav-services-link"],
    ["#why-nevada", t.nav.why, "nav-why-link"],
    ["#who-we-serve", t.nav.serve, "nav-serve-link"],
    ["#how-we-help", t.nav.process, "nav-process-link"],
    ["#about", t.nav.about, "nav-about-link"],
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-[70] transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled || open
          ? "border-b border-white/10 bg-[#0A1128]/80 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="tricolor-line w-full" data-testid="brand-ribbon" />
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6 lg:px-10">
        <button
          data-testid="header-logo"
          onClick={() => go("#hero")}
          className="group flex items-center gap-3"
        >
          <img
            src="/koam-logo.png"
            alt="KOAM Las Vegas Business Consultants LLC logo"
            className="h-14 w-auto rounded-sm transition-transform duration-300 group-hover:scale-[1.03]"
          />
        </button>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="desktop-nav">
          {links.map(([id, label, testId]) => (
            <button
              key={id}
              data-testid={testId}
              onClick={() => go(id)}
              className="gold-underline pb-1 text-sm font-light text-white/70 transition-colors duration-300 hover:text-white"
            >
              {label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div
            className="flex items-center border border-white/15 text-xs font-medium"
            data-testid="language-switcher"
          >
            <button
              data-testid="lang-toggle-en"
              onClick={() => setLang("en")}
              className={`px-3 py-2 transition-colors duration-300 ${
                lang === "en" ? "bg-[#D4AF37] text-[#0A1128]" : "text-white/60 hover:text-white"
              }`}
            >
              EN
            </button>
            <button
              data-testid="lang-toggle-ko"
              onClick={() => setLang("ko")}
              className={`px-3 py-2 font-kr transition-colors duration-300 ${
                lang === "ko" ? "bg-[#D4AF37] text-[#0A1128]" : "text-white/60 hover:text-white"
              }`}
            >
              한국어
            </button>
          </div>

          <button
            data-testid="header-consultation-cta"
            onClick={() => go("#contact")}
            className="group hidden items-center gap-2 bg-[#D4AF37] px-5 py-2.5 text-sm font-semibold text-[#0A1128] transition-colors duration-300 hover:bg-white lg:flex"
          >
            {t.nav.cta}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>

          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="flex h-10 w-10 items-center justify-center border border-white/15 text-white lg:hidden"
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-nav"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-white/10 bg-[#0A1128]/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {links.map(([id, label, testId]) => (
                <button
                  key={id}
                  data-testid={`mobile-${testId}`}
                  onClick={() => go(id)}
                  className="py-3 text-left font-display text-2xl font-light text-white/80 transition-colors duration-300 hover:text-[#D4AF37]"
                >
                  {label}
                </button>
              ))}
              <button
                data-testid="mobile-consultation-cta"
                onClick={() => go("#contact")}
                className="mt-4 flex items-center justify-center gap-2 bg-[#D4AF37] px-5 py-4 text-sm font-semibold text-[#0A1128]"
              >
                {t.nav.cta}
                <ArrowUpRight className="h-4 w-4" />
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
